/**
 * fogConfig — pure placement math for the fog effect layer.
 * Deterministic seeding (the same Math.sin trick as atmospherePlacement) so a
 * level's haze bands sit at stable spots across reloads. Split out so the
 * scatter is unit-testable without Pixi; the sprite-driving glue (tweens) lives
 * in fogEffect.ts and is VQA-verified by the foreground.
 */
import { seededRand, type Region } from '../atmospherePlacement';
import type { FogSpec } from '../../../../config/atmosphere/effectLayer.types';
import { lerp } from '../../../../shared/utils/MathUtils';

/** Defaults so a partial FogSpec still places sensible big, soft haze bands. */
const DEFAULTS = {
  yMin: 0.4,
  yMax: 0.9,
  sizeMin: 260,
  sizeMax: 520,
} as const;

export interface PlacedFogBand {
  x: number;
  y: number;
  size: number;
  color: number;
  /** Horizontal drift radius (px) — band tweens x ± driftPx. */
  driftPx: number;
  /** Drift/alpha cycle length (ms), seeded per band in [periodMinMs..periodMaxMs]. */
  periodMs: number;
  alphaMin: number;
  alphaMax: number;
}

/**
 * Place `spec.bands` haze bands across the region: x spread across the full
 * width, y within the [yMin..yMax] fraction of the region height. Size, period
 * and drift direction are seeded per band so the bands breathe asynchronously.
 */
export function placeFog(spec: FogSpec, r: Region, baseSeed: number): PlacedFogBand[] {
  const yMin = spec.yMin ?? DEFAULTS.yMin;
  const yMax = spec.yMax ?? DEFAULTS.yMax;
  const sizeMin = spec.sizeMin ?? DEFAULTS.sizeMin;
  const sizeMax = spec.sizeMax ?? DEFAULTS.sizeMax;

  const out: PlacedFogBand[] = [];
  for (let i = 0; i < spec.bands; i += 1) {
    const s = baseSeed + 300 + i * 11.17;
    // Drift sign seeded so neighbouring bands slide opposite ways.
    const dir = seededRand(s + 4) < 0.5 ? -1 : 1;
    out.push({
      x: lerp(r.x0, r.x1, seededRand(s)),
      y: lerp(r.y0 + (r.y1 - r.y0) * yMin, r.y0 + (r.y1 - r.y0) * yMax, seededRand(s + 1)),
      size: lerp(sizeMin, sizeMax, seededRand(s + 2)),
      color: spec.color,
      driftPx: spec.driftPx * dir,
      periodMs: lerp(spec.periodMinMs, spec.periodMaxMs, seededRand(s + 3)),
      alphaMin: spec.alphaMin,
      alphaMax: spec.alphaMax,
    });
  }
  return out;
}
